import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { NumberField } from '@/components/form/fields/text/NumberInput';
import { Form } from '@/components/form/Form';
import { Loading } from '@/components/loading/Loading';
import { Modal } from '@/components/modal/Modal';
import { ModalProps } from '@/components/modal/useModal';
import { Card } from '@/components/sections/Card';
import { useCreateMspCreditCheck, useMspCreditCheck } from '@/services/api/hooks/credit';
import { usePartner } from '@/services/api/hooks/partners';
import { useCurrencyIdentifier } from '@/utils/price';
import { zodResolver } from '@hookform/resolvers/zod';
import { FunctionComponent, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

const MspEditCreditCheckFormFields = z.object({
  limit: z.number()
});

type MspEditCreditCheckFormFields = z.infer<typeof MspEditCreditCheckFormFields>;

const MspEditCreditCheckModal: FunctionComponent<ModalProps<{ mspId: string; creditCheckId?: string }>> = props => {
  const EditCreditCheck = () => {
    const mspId = props.props?.mspId;
    if (!mspId) throw new Error('Missing msp id');

    const [msp] = usePartner(mspId);

    const currencyIdentifier = useCurrencyIdentifier(msp.region?.code, false);

    const [creditCheck] = useMspCreditCheck(mspId, props.props?.creditCheckId);

    const createCreditCheck = useCreateMspCreditCheck();

    const methods = useForm<MspEditCreditCheckFormFields>({
      resolver: zodResolver(MspEditCreditCheckFormFields),
      defaultValues: {
        limit: (creditCheck && creditCheck?.creditLimit) ?? 0
      }
    });

    const onSubmit = async (data: MspEditCreditCheckFormFields) => {
      await createCreditCheck.mutateAsync({
        mspId,
        creditLimit: data.limit
      });
      props.close();
    };

    const errorObj = createCreditCheck.error as Error | undefined;
    const error = errorObj ? errorObj.message : '';

    return (
      <Card className="w-full p-8">
        <Modal.Controls {...props}>
          <h1 className="text-2xl font-semibold">Edit Customer Credit</h1>
        </Modal.Controls>
        <Form className="flex w-full flex-col space-y-4 py-3" methods={methods} onSubmit={onSubmit}>
          <div className="flex-col space-y-1">
            <p>Name</p>
            <p className="font-semibold">{msp.name}</p>
          </div>

          <NumberField id="limit" label={`Credit Limit (${currencyIdentifier})`} />

          {error && <p className="text-red-500">{error}</p>}

          <div className="flex justify-end pt-2">
            <Button
              loading={createCreditCheck.isLoading}
              className="w-fit"
              variant={ButtonVariant.Primary}
              type="submit"
            >
              Submit
            </Button>
          </div>
        </Form>
      </Card>
    );
  };

  return (
    <Modal className="md:max-w-xl" {...props}>
      <Suspense fallback={<Loading />}>
        <EditCreditCheck />
      </Suspense>
    </Modal>
  );
};

export default MspEditCreditCheckModal;
